import { impactGoalsKey } from 'apis/CompanyAPI/companies/useImpactGoals';
import { useUpdateCompany } from 'apis/CompanyAPI/companies/useUpdateCompany';
import useSaveExecutiveSummary from 'domain/companies/ExecutiveSummary/useSaveExecutiveSummary';
import { MarketPotentialFormInput, useSaveMarketPotential } from 'domain/companies/MarketPotential/utils';
import { ImpactGoal } from 'types';
import { CompanyProfile, ExecutiveSummaryDTO, UpdateCompanyDTO } from 'types/company';
import { fetchLocation } from 'apis/MapboxAPI';
import { invalidate } from 'hooks/useSWR';
import { MarketPotential, marketPotentialKey } from 'apis/CompanyAPI/companies/useMarketPotential';
import { CompanyOnboardingSuggestions } from 'types/onboarding';
import { ProfilePart } from 'apis/CompanyAPI/communities/applications/communityApplicationsApi';
import useUpdateImpactGoals from './useUpdateImpactGoals';

export default function usePatchCompanyProfile(
  company: CompanyProfile,
  impactGoals: ImpactGoal[],
  executiveSummary: ExecutiveSummaryDTO,
  marketPotential: MarketPotential | undefined,
  profileParts: ProfilePart[],
) {
  const [saveGoals] = useUpdateImpactGoals(company.id, {
    onSuccess: goals => {
      invalidate(impactGoalsKey(company.id), goals);
    },
  });

  const [updateCompany] = useUpdateCompany(company.id, company.slug, {});
  const [saveExecutiveSummary] = useSaveExecutiveSummary(company, {}, false);
  const [onSaveMarketPotential] = useSaveMarketPotential(
    company.id,
    {
      onSuccess: () => invalidate(marketPotentialKey(company.id)),
    },
    false,
  );

  async function updateProfile(suggestions: CompanyOnboardingSuggestions) {
    const locationResponse = suggestions.location ? await fetchLocation(suggestions.location) : undefined;

    const updateCompanyData: UpdateCompanyDTO = {
      mission:
        profileParts.includes('Mission') && !company.mission && suggestions.mission ? suggestions.mission : undefined,
      employees:
        profileParts.includes('Employees') && !company.employees && suggestions.employees
          ? suggestions.employees
          : undefined,
      established:
        profileParts.includes('EstablishedYear') && !company.established && suggestions.founded
          ? `${suggestions.founded}`
          : undefined,
      website:
        profileParts.includes('Website') && !company.website && suggestions.website ? suggestions.website : undefined,
      logoURL:
        profileParts.includes('Branding') && !company.logoURL && suggestions.logoURL ? suggestions.logoURL : undefined,
      location:
        profileParts.includes('Location') && !company.location && locationResponse ? locationResponse : undefined,
      fundingStage:
        profileParts.includes('FundingStage') && !company.fundingStage && suggestions.fundingStage
          ? suggestions.fundingStage.id
          : undefined,
    };

    const labels = {
      industries:
        profileParts.includes('Industries') &&
        company.industries.values.length === 0 &&
        (suggestions.industries?.length || 0) > 0
          ? suggestions.industries
          : undefined,
      productStage:
        profileParts.includes('ProductStage') && company.stages.values.length === 0 && suggestions.productStage
          ? suggestions.productStage
          : undefined,
      fundingStage:
        profileParts.includes('FundingStage') && !company.fundingStage && suggestions.fundingStage
          ? suggestions.fundingStage
          : undefined,
    };

    const executiveSummaryDTO: ExecutiveSummaryDTO = {
      problem:
        profileParts.includes('Problem') && !executiveSummary.problem && suggestions.problem
          ? suggestions.problem
          : undefined,
      solution:
        profileParts.includes('Solution') && !executiveSummary.solution && suggestions.solution
          ? suggestions.solution
          : undefined,
      teamDescription:
        profileParts.includes('TeamDescription') && !executiveSummary.teamDescription && suggestions.teamDescription
          ? suggestions.teamDescription
          : undefined,
      unfairAdvantage:
        profileParts.includes('WhyInvestInUs') && !executiveSummary.unfairAdvantage && suggestions.unfairAdvantage
          ? suggestions.unfairAdvantage
          : undefined,
    };

    if (profileParts.includes('MarketPotential')) {
      const dto: MarketPotentialFormInput = {
        geographicalMarketPotential:
          suggestions.geographicalMarketPotential && !marketPotential?.geographicalMarketPotential
            ? suggestions.geographicalMarketPotential
            : [],
        currentMarketFocus:
          suggestions.currentMarketFocus && !marketPotential?.currentMarketFocus ? suggestions.currentMarketFocus : [],
        estimatedAvailableMarketValue:
          suggestions.estimatedAvailableMarket && !marketPotential?.estimatedAvailableMarket
            ? suggestions.estimatedAvailableMarket.value
            : undefined,
        estimatedMarketCurrency:
          suggestions.estimatedAvailableMarket?.currency && !marketPotential?.estimatedAvailableMarket
            ? suggestions.estimatedAvailableMarket.currency
            : '',
        estimatedServiceableMarketValue:
          suggestions.estimatedServiceableMarket && !marketPotential?.estimatedServiceableMarket
            ? suggestions.estimatedServiceableMarket.value
            : undefined,
        marketDescription:
          suggestions.marketPotentialDescription && !marketPotential?.description
            ? suggestions.marketPotentialDescription
            : undefined,
      };

      onSaveMarketPotential(dto);
    }

    if (profileParts.includes('ImpactGoals') && impactGoals.length === 0 && suggestions.impactGoals) {
      saveGoals(suggestions.impactGoals.map(goal => goal.id));
    }

    updateCompany({ company: updateCompanyData, labels });
    saveExecutiveSummary(executiveSummaryDTO);
  }

  return { updateBasedOnSuggestions: updateProfile };
}
